(function () {
  const FILTER_CONFIGS = [
    { key: 'brands', label: 'Brand', param: 'cf_brand' },
    { key: 'colors', label: 'Color', param: 'cf_color' },
    { key: 'sizes', label: 'Size', param: 'cf_size' },
  ];

  const COLLECTION_FILTER_CACHE = new Map();

  const COLOR_LABELS = {
    black: 'Black',
    blue: 'Blue',
    brown: 'Brown',
    burgundy: 'Burgundy',
    clear: 'Clear',
    gold: 'Gold',
    gray: 'Gray',
    grey: 'Grey',
    green: 'Green',
    lilac: 'Lilac',
    navy: 'Navy',
    nude: 'Nude',
    pink: 'Pink',
    purple: 'Purple',
    red: 'Red',
    rose: 'Rose',
    silver: 'Silver',
    tan: 'Tan',
    teal: 'Teal',
    white: 'White',
  };

  const SIZE_LABELS = {
    xs: 'XS',
    s: 'S',
    m: 'M',
    l: 'L',
    xl: 'XL',
    xxl: 'XXL',
    xxxl: 'XXXL',
    '2x': '2X',
    '3x': '3X',
    '4x': '4X',
    '5x': '5X',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    os: 'OS',
  };

  function createEmptyMeta() {
    return { brands: [], colors: [], sizes: [], prices: [], options: [] };
  }

  function createLabelMaps() {
    return {
      brands: new Map(),
      colors: new Map(),
      sizes: new Map(),
      prices: new Map(),
      options: new Map(),
    };
  }

  function slugify(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function pushUnique(list, value) {
    if (value && !list.includes(value)) list.push(value);
  }

  function getPrettyColor(value) {
    return COLOR_LABELS[slugify(value)] || '';
  }

  function getPrettySize(value) {
    return SIZE_LABELS[slugify(value)] || '';
  }

  function classifyOptionLabel(label) {
    const cleanedLabel = (label || '').replace(/\s+/g, ' ').trim();
    const meta = createEmptyMeta();

    if (!cleanedLabel) return meta;

    const segments = cleanedLabel.includes(' / ') ? cleanedLabel.split(' / ') : [cleanedLabel];

    segments.forEach((segment) => {
      const cleanedSegment = segment.trim();
      if (!cleanedSegment) return;

      const slashParts = cleanedSegment.includes('/')
        ? cleanedSegment.split('/').map((part) => part.trim()).filter(Boolean)
        : [cleanedSegment];

      if (slashParts.length > 1) {
        const colorParts = slashParts.map(getPrettyColor);
        if (colorParts.every(Boolean)) {
          colorParts.forEach((value) => pushUnique(meta.colors, value));
          return;
        }

        const sizeParts = slashParts.map(getPrettySize);
        if (sizeParts.every(Boolean)) {
          sizeParts.forEach((value) => pushUnique(meta.sizes, value));
          return;
        }
      }

      const prettyColor = getPrettyColor(cleanedSegment);
      if (prettyColor) {
        pushUnique(meta.colors, prettyColor);
        return;
      }

      const prettySize = getPrettySize(cleanedSegment);
      if (prettySize) {
        pushUnique(meta.sizes, prettySize);
        return;
      }

      pushUnique(meta.options, cleanedSegment);
    });

    if (!meta.colors.length && !meta.sizes.length && !meta.options.length) {
      pushUnique(meta.options, cleanedLabel);
    }

    return meta;
  }

  function readStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const state = {};

    FILTER_CONFIGS.forEach(({ key, param }) => {
      state[key] = new Set(
        (params.get(param) || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      );
    });

    return state;
  }

  function getBaseQueryParams() {
    const params = new URLSearchParams(window.location.search);

    FILTER_CONFIGS.forEach(({ param }) => {
      params.delete(param);
    });

    params.delete('page');
    params.delete('section_id');

    return params;
  }

  function getProductCountLabel(count) {
    return `${count} product${count === 1 ? '' : 's'}`;
  }

  function getPrimaryCardUrl(cardItem) {
    return cardItem.querySelector('.card__heading a')?.getAttribute('href') || '#';
  }

  function getRecordPriority(record) {
    return (record.hasAvailable ? 100000 : 0) + record.productCount * 100 + record.optionCount;
  }

  function buildRecordFromCard(card, sortIndex) {
    const wrapper = card.classList.contains('card-wrapper') ? card : card.querySelector('.card-wrapper') || card;
    const groupHost = card.dataset.groupedProductCard !== undefined ? card : card.closest('[data-grouped-product-card]') || card;

    const rawOptionLabels = (groupHost.dataset.familyOptions || wrapper.dataset.options || card.dataset.familyOptions || card.dataset.options || '')
      .split('||')
      .concat(Array.from(card.querySelectorAll('[data-option-value], .product-form__input label, .swatch-input__input')).map((el) => el.value || el.textContent || ''))
      .map((value) => value.trim())
      .filter(Boolean);

    const meta = createEmptyMeta();
    const labels = createLabelMaps();

    rawOptionLabels.forEach((label) => {
      const classified = classifyOptionLabel(label);

      ['colors', 'sizes', 'options'].forEach((key) => {
        classified[key].forEach((displayLabel) => {
          const slug = slugify(displayLabel);
          if (!slug) return;

          pushUnique(meta[key], slug);

          if (!labels[key].has(slug)) {
            labels[key].set(slug, displayLabel);
          }
        });
      });
    });

    const vendorText = (wrapper.dataset.vendor || groupHost.dataset.vendor || card.dataset.vendor || card.querySelector('.caption-with-letter-spacing, .card__vendor')?.textContent || '').trim();
    if (vendorText) {
      const vendorSlug = slugify(vendorText);
      if (vendorSlug) {
        pushUnique(meta.brands, vendorSlug);
        if (!labels.brands.has(vendorSlug)) {
          labels.brands.set(vendorSlug, vendorText);
        }
      }
    }

    const priceRaw = wrapper.dataset.price || groupHost.dataset.price || card.dataset.price || card.querySelector('.price-item--sale, .price-item--regular, .price-item')?.textContent || '';
    const priceAmount = parseFloat(`${priceRaw}`.replace(/[^0-9.]/g, ''));
    if (!isNaN(priceAmount) && priceAmount > 0) {
      let priceSlug = '';
      let priceLabel = '';

      if (priceAmount < 25) {
        priceSlug = 'under-25';
        priceLabel = 'Under $25';
      } else if (priceAmount <= 50) {
        priceSlug = '25-to-50';
        priceLabel = '$25 - $50';
      } else if (priceAmount <= 100) {
        priceSlug = '50-to-100';
        priceLabel = '$50 - $100';
      } else {
        priceSlug = 'over-100';
        priceLabel = 'Over $100';
      }

      if (priceSlug) {
        pushUnique(meta.prices, priceSlug);
        if (!labels.prices.has(priceSlug)) {
          labels.prices.set(priceSlug, priceLabel);
        }
      }
    }

    return {
      familyKey: groupHost.dataset.familyKey || wrapper.dataset.familyKey || card.dataset.familyKey || groupHost.dataset.familyTitle || `family-${sortIndex}`,
      title: groupHost.dataset.familyTitle || wrapper.dataset.familyTitle || card.dataset.familyTitle || card.querySelector('.card__heading')?.textContent?.trim() || '',
      html: card.outerHTML,
      optionLabels: rawOptionLabels,
      meta,
      labels,
      optionCount: rawOptionLabels.length,
      productCount: parseInt(
        groupHost.dataset.familyProductCount || wrapper.dataset.familyProductCount || card.dataset.familyProductCount || `${rawOptionLabels.length}` || '1',
        10
      ) || 1,
      hasAvailable:
        groupHost.dataset.familyHasAvailable === 'true' || wrapper.dataset.familyHasAvailable === 'true' || card.dataset.familyHasAvailable === 'true' || !card.querySelector('.card-collection-stock[disabled]'),
      sortIndex,
      sourcePriority: 0,
    };
  }

  function buildRecordsFromCards(cards, pageNumber) {
    const uniqueCards = [];
    Array.from(cards).forEach((card) => {
      const topCard = card.closest('li.grid__item') || card.closest('[data-grouped-product-card]') || card;
      if (!uniqueCards.includes(topCard)) {
        uniqueCards.push(topCard);
      }
    });

    return uniqueCards
      .map((card, index) => buildRecordFromCard(card, (pageNumber - 1) * 1000 + index))
      .filter(Boolean);
  }

  function buildRecordsFromHtml(html, pageNumber) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    return buildRecordsFromCards(parsedHtml.querySelectorAll('#product-grid > li, #product-grid > .card-wrapper, [data-grouped-product-card], .card-wrapper, .grid__item'), pageNumber);
  }

  function cloneRecord(record) {
    return {
      familyKey: record.familyKey,
      title: record.title,
      html: record.html,
      optionLabels: [...record.optionLabels],
      meta: {
        brands: [...record.meta.brands],
        colors: [...record.meta.colors],
        sizes: [...record.meta.sizes],
        prices: [...record.meta.prices],
        options: [...record.meta.options],
      },
      labels: {
        brands: new Map(record.labels.brands),
        colors: new Map(record.labels.colors),
        sizes: new Map(record.labels.sizes),
        prices: new Map(record.labels.prices),
        options: new Map(record.labels.options),
      },
      optionCount: record.optionCount,
      productCount: record.productCount,
      hasAvailable: record.hasAvailable,
      sortIndex: record.sortIndex,
      sourcePriority: getRecordPriority(record),
    };
  }

  function updateGroupedBadge(cardItem, record) {
    const media = cardItem.querySelector('.card__media');
    let badge = cardItem.querySelector('.product-family-badge');

    if (record.productCount > 1) {
      if (!badge && media) {
        badge = document.createElement('div');
        badge.className = 'product-family-badge';

        const count = document.createElement('span');
        count.className = 'product-family-badge__count';

        const label = document.createElement('span');
        label.className = 'product-family-badge__label';
        label.textContent = 'options';

        badge.appendChild(count);
        badge.appendChild(label);
        media.appendChild(badge);
      }

      if (badge) {
        badge.setAttribute('aria-label', `Available in ${record.productCount} options`);

        const countElement = badge.querySelector('.product-family-badge__count');
        if (countElement) {
          countElement.textContent = `${record.productCount}`;
        }

        const labelElement = badge.querySelector('.product-family-badge__label');
        if (labelElement) {
          labelElement.textContent = 'options';
        }
      }
    } else if (badge) {
      badge.remove();
    }
  }

  function updateGroupedTitle(cardItem, record) {
    if (record.productCount <= 1 || !record.title) return;

    cardItem.querySelectorAll('.card__heading a').forEach((link) => {
      link.textContent = record.title;
    });
  }

  function updateGroupedActions(cardItem, record, labels) {
    const footer = cardItem.querySelector('.card-collection-footer');
    if (!footer || record.productCount <= 1) return;

    const wrapper = cardItem.querySelector('.product-card-wrapper');
    if (wrapper) {
      wrapper.classList.add('product-card-wrapper--grouped');
    }

    footer
      .querySelectorAll('product-form, modal-opener, quick-add-modal, .card-collection-cart, .card-collection-stock')
      .forEach((node) => node.remove());

    if (record.hasAvailable) {
      const link = document.createElement('a');
      link.href = getPrimaryCardUrl(cardItem);
      link.className = 'card-collection-cart card-collection-cart--link';
      link.setAttribute('aria-label', `${labels.chooseOptions} ${record.title}`.trim());
      link.textContent = labels.chooseOptions;
      footer.appendChild(link);
      return;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'card-collection-stock';
    button.disabled = true;
    button.setAttribute('aria-label', labels.soldOut);
    button.textContent = labels.soldOut;
    footer.appendChild(button);
  }

  function finalizeRecordHtml(record, labels) {
    const template = document.createElement('template');
    template.innerHTML = record.html.trim();

    const cardItem = template.content.firstElementChild;
    if (!cardItem) return record.html;

    cardItem.dataset.familyKey = record.familyKey;
    cardItem.dataset.familyTitle = record.title;
    cardItem.dataset.familyOptions = record.optionLabels.join('||');
    cardItem.dataset.familyOptionCount = `${record.optionCount}`;
    cardItem.dataset.familyProductCount = `${record.productCount}`;
    cardItem.dataset.familyHasAvailable = record.hasAvailable ? 'true' : 'false';

    updateGroupedBadge(cardItem, record);
    updateGroupedTitle(cardItem, record);
    updateGroupedActions(cardItem, record, labels);

    return cardItem.outerHTML;
  }

  function mergeCollectionRecords(records, labels) {
    const mergedRecords = new Map();

    records.forEach((record) => {
      const familyKey = record.familyKey || `${record.sortIndex}`;

      if (!mergedRecords.has(familyKey)) {
        mergedRecords.set(familyKey, cloneRecord(record));
        return;
      }

      const existing = mergedRecords.get(familyKey);

      existing.sortIndex = Math.min(existing.sortIndex, record.sortIndex);
      existing.productCount += record.productCount;
      existing.hasAvailable = existing.hasAvailable || record.hasAvailable;

      if (!existing.title && record.title) {
        existing.title = record.title;
      }

      record.optionLabels.forEach((label) => {
        pushUnique(existing.optionLabels, label);
      });

      FILTER_CONFIGS.forEach(({ key }) => {
        record.meta[key].forEach((slug) => {
          pushUnique(existing.meta[key], slug);
        });

        record.labels[key].forEach((displayLabel, slug) => {
          if (!existing.labels[key].has(slug)) {
            existing.labels[key].set(slug, displayLabel);
          }
        });
      });

      const nextPriority = getRecordPriority(record);
      if (nextPriority > existing.sourcePriority) {
        existing.html = record.html;
        existing.sourcePriority = nextPriority;
      }
    });

    return Array.from(mergedRecords.values())
      .sort((left, right) => left.sortIndex - right.sortIndex)
      .map((record) => {
        record.optionCount = record.optionLabels.length;
        record.html = finalizeRecordHtml(record, labels);
        return record;
      });
  }

  class CollectionOptionFilters {
    constructor(container) {
      this.container = container;
      this.isDestroyed = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleSearchInput = this.handleSearchInput.bind(this);

      this.container.addEventListener('click', this.handleClick);
      this.container.addEventListener('change', this.handleChange);
      this.container.addEventListener('input', this.handleSearchInput);
      this.refresh();
    }

    destroy() {
      this.isDestroyed = true;
      this.container.removeEventListener('click', this.handleClick);
      this.container.removeEventListener('change', this.handleChange);
      this.container.removeEventListener('input', this.handleSearchInput);
    }

    refresh() {
      this.gridContainer = document.getElementById('ProductGridContainer');
      this.grid = document.getElementById('product-grid');
      this.productCount = document.getElementById('ProductCount');
      this.productCountDesktop = document.getElementById('ProductCountDesktop');

      if (!this.gridContainer || !this.grid) {
        this.container.hidden = true;
        return;
      }

      this.collectionRoot = this.grid.closest('.collection');
      this.gridItemsContainer =
        this.grid.matches('ul, ol') ? this.grid : this.grid.querySelector('.product-grid') || this.grid;
      this.paginationWrapper = this.collectionRoot?.querySelector('.pagination-wrapper') || null;
      this.chooseOptionsLabel = this.gridContainer.dataset.chooseOptionsLabel || 'Choose options';
      this.soldOutLabel = this.gridContainer.dataset.soldOutLabel || 'Out of stock';
      this.sectionId = this.grid.dataset.id || this.gridItemsContainer.dataset.id || '';
      this.currentPage = parseInt(this.gridContainer.dataset.currentPage || '1', 10) || 1;
      this.totalPages = parseInt(this.gridContainer.dataset.totalPages || '1', 10) || 1;
      this.baseParams = getBaseQueryParams();
      this.state = readStateFromUrl();
      this.pageRecords = buildRecordsFromCards(
        this.gridItemsContainer.querySelectorAll('#product-grid > li, #product-grid > .card-wrapper, [data-grouped-product-card], .card-wrapper, .grid__item'),
        this.currentPage
      );
      this.records = this.pageRecords;
      this.originalGridHTML = this.gridItemsContainer.innerHTML;
      this.originalCountHTML = this.productCount ? this.productCount.innerHTML : '';
      this.originalCountDesktopHTML = this.productCountDesktop ? this.productCountDesktop.innerHTML : '';
      this.hasCollectionData = this.totalPages <= 1;
      this.isLoadingCollection = false;
      this.gridShowsOriginal = true;
      this.hasRequestedCollectionRecords = false;

      this.rebuildAvailableValues();
      this.normalizeState();
      this.render();
      this.applyFilters();
      this.syncHiddenInputs();
      this.updateLinkedUrls();
      this.updateUrl();

      const hasActiveFiltersOnLoad = FILTER_CONFIGS.some(({ key }) => this.state[key].size > 0);
      if (hasActiveFiltersOnLoad) this.ensureCollectionRecordsLoaded();
    }

    rebuildAvailableValues() {
      this.availableValues = createLabelMaps();

      this.records.forEach((record) => {
        FILTER_CONFIGS.forEach(({ key }) => {
          record.labels[key].forEach((displayLabel, slug) => {
            if (!this.availableValues[key].has(slug)) {
              this.availableValues[key].set(slug, displayLabel);
            }
          });
        });
      });
    }

    normalizeState() {
      FILTER_CONFIGS.forEach(({ key }) => {
        const entries = Array.from(this.availableValues[key].entries());

        if (this.shouldSuppressGroup(key, entries)) {
          this.availableValues[key] = new Map();
          this.state[key].clear();
          return;
        }

        this.state[key] = new Set(
          Array.from(this.state[key]).filter((slug) => this.availableValues[key].has(slug))
        );
      });
    }

    shouldSuppressGroup(filterKey, entries) {
      return false;
    }

    ensureCollectionRecordsLoaded() {
      if (this.hasRequestedCollectionRecords) return;
      this.hasRequestedCollectionRecords = true;
      this.loadCollectionRecords();
    }

    async loadCollectionRecords() {
      if (!this.sectionId || this.totalPages <= 1) return;

      const cacheKey = `${window.location.pathname}::${this.sectionId}::${this.baseParams.toString()}::${this.totalPages}`;
      this.requestKey = cacheKey;

      if (COLLECTION_FILTER_CACHE.has(cacheKey)) {
        this.applyCollectionRecords(cacheKey, COLLECTION_FILTER_CACHE.get(cacheKey));
        return;
      }

      this.isLoadingCollection = true;
      this.applyFilters();

      const pageRequests = [];

      for (let page = 1; page <= this.totalPages; page += 1) {
        if (page === this.currentPage) continue;

        const params = new URLSearchParams(this.baseParams);
        params.set('page', `${page}`);
        params.set('section_id', this.sectionId);

        pageRequests.push(
          fetch(`${window.location.pathname}?${params.toString()}`)
            .then((response) => response.text())
            .then((html) => buildRecordsFromHtml(html, page))
            .catch(() => [])
        );
      }

      const fetchedRecords = await Promise.all(pageRequests);

      if (this.isDestroyed || this.requestKey !== cacheKey) return;

      const mergedRecords = mergeCollectionRecords(
        [...this.pageRecords, ...fetchedRecords.flat()],
        {
          chooseOptions: this.chooseOptionsLabel,
          soldOut: this.soldOutLabel,
        }
      );

      COLLECTION_FILTER_CACHE.set(cacheKey, mergedRecords);
      this.applyCollectionRecords(cacheKey, mergedRecords);
    }

    applyCollectionRecords(cacheKey, records) {
      if (this.isDestroyed || this.requestKey !== cacheKey) return;

      this.records = records;
      this.hasCollectionData = true;
      this.isLoadingCollection = false;
      this.rebuildAvailableValues();
      this.normalizeState();
      this.rerender();
    }

    cloneState() {
      const state = {};

      FILTER_CONFIGS.forEach(({ key }) => {
        state[key] = new Set(this.state[key]);
      });

      return state;
    }

    cardMatchesState(record, state) {
      const meta = record.meta || createEmptyMeta();

      return FILTER_CONFIGS.every(({ key }) => {
        const selectedValues = state[key];
        if (!selectedValues || !selectedValues.size) return true;

        return meta[key].some((value) => selectedValues.has(value));
      });
    }

    getOptionCount(filterKey, filterValue) {
      const state = this.cloneState();
      state[filterKey].add(filterValue);

      return this.records.reduce((count, record) => {
        return count + (this.cardMatchesState(record, state) ? 1 : 0);
      }, 0);
    }

    render() {
      const openGroups = new Set(
        Array.from(this.container.querySelectorAll('details[open]')).map((el) => el.dataset.optionFilterGroup)
      );

      this.container.innerHTML = '';

      const fragment = document.createDocumentFragment();
      let hasVisibleGroups = false;

      FILTER_CONFIGS.forEach((config) => {
        const selectedValues = this.state[config.key];
        const entries = Array.from(this.availableValues[config.key].entries());
        const shouldRenderGroup = entries.length > 0;

        if (!shouldRenderGroup) return;

        hasVisibleGroups = true;

        const details = document.createElement('details');
        details.className = 'facets__disclosure-vertical js-filter collection-option-filter';
        details.dataset.optionFilterGroup = config.key;
        details.open = openGroups.has(config.key);
        details.addEventListener('toggle', () => {
          if (details.open) this.ensureCollectionRecordsLoaded();
        });

        const summary = document.createElement('summary');
        summary.className = 'facets__summary caption-large focus-offset';
        summary.setAttribute(
          'aria-label',
          selectedValues.size > 0 ? `${config.label} (${selectedValues.size} selected)` : config.label
        );
        summary.innerHTML = `
          <div>
            <span class="facets__summary-label">
              ${config.label}
              <span class="facets__selected${selectedValues.size === 0 ? ' hidden' : ''}">(${selectedValues.size})</span>
            </span>
            <span class="svg-wrapper facets__summary-icon" aria-hidden="true"></span>
          </div>
        `;

        const display = document.createElement('div');
        display.className = 'parent-display facets__display-vertical';

        if (entries.length > 5) {
          const searchContainer = document.createElement('div');
          searchContainer.className = 'collection-option-filter__search-wrapper';
          searchContainer.innerHTML = `
            <input
              type="text"
              class="collection-option-filter__search-input"
              placeholder="Search ${config.label.toLowerCase()}..."
              aria-label="Search ${config.label.toLowerCase()}"
              data-option-filter-search="${config.key}"
            >
          `;
          display.appendChild(searchContainer);
        }

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'facets-wrap parent-wrap facets-wrap-vertical';

        const legend = document.createElement('legend');
        legend.className = 'visually-hidden';
        legend.textContent = config.label;
        fieldset.appendChild(legend);

        const list = document.createElement('ul');
        list.className = 'facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled';
        list.setAttribute('role', 'list');

        entries.forEach(([slug, displayLabel]) => {
          const isSelected = selectedValues.has(slug);
          const optionCount = this.getOptionCount(config.key, slug);
          const inputId = `Filter-${config.param}-${slug}`;

          const li = document.createElement('li');
          li.className = `list-menu__item facets__item${optionCount === 0 && !isSelected ? ' disabled' : ''}`;

          const label = document.createElement('label');
          label.htmlFor = inputId;
          label.className = `facets__label facet-checkbox${optionCount === 0 && !isSelected ? ' disabled' : ''}${isSelected ? ' active' : ''}`;

          const input = document.createElement('input');
          input.type = 'checkbox';
          input.name = config.param;
          input.value = slug;
          input.id = inputId;
          input.dataset.optionFilterValue = slug;
          input.dataset.filterKey = config.key;
          if (isSelected) input.checked = true;
          if (optionCount === 0 && !isSelected) input.disabled = true;

          label.appendChild(input);

          // Square SVG
          const squareSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          squareSvg.setAttribute('class', 'icon icon-square');
          squareSvg.setAttribute('width', '16');
          squareSvg.setAttribute('height', '16');
          squareSvg.setAttribute('viewBox', '0 0 16 16');
          squareSvg.setAttribute('aria-hidden', 'true');
          squareSvg.setAttribute('focusable', 'false');
          squareSvg.innerHTML = '<rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect>';
          label.appendChild(squareSvg);

          // Checkmark SVG inside wrapper
          const checkmarkWrapper = document.createElement('div');
          checkmarkWrapper.className = 'svg-wrapper';
          checkmarkWrapper.innerHTML = `
            <svg class="icon icon-checkmark" width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true" focusable="false">
              <path d="M1.5 3.5L4.5 6.5L9.5 1.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          `;
          label.appendChild(checkmarkWrapper);

          // Text label & count
          const textSpan = document.createElement('span');
          textSpan.className = 'facet-checkbox__text';
          textSpan.setAttribute('aria-hidden', 'true');
          textSpan.innerHTML = `<span class="facet-checkbox__text-label">${displayLabel}</span> (${optionCount})`;
          label.appendChild(textSpan);

          li.appendChild(label);
          list.appendChild(li);
        });

        fieldset.appendChild(list);
        display.appendChild(fieldset);
        details.appendChild(summary);
        details.appendChild(display);
        fragment.appendChild(details);
      });

      const clearButton = document.createElement('button');
      clearButton.type = 'button';
      clearButton.className = 'active-facets__button-remove underlined-link collection-option-filters__clear';
      clearButton.dataset.optionFilterClear = 'true';
      clearButton.textContent = 'Clear all filters';
      clearButton.hidden = true;
      fragment.appendChild(clearButton);

      const status = document.createElement('span');
      status.className = 'collection-option-filters__status';
      status.dataset.optionFilterStatus = 'true';
      status.hidden = true;
      fragment.appendChild(status);

      this.container.appendChild(fragment);
      this.clearButton = this.container.querySelector('[data-option-filter-clear]');
      this.statusElement = this.container.querySelector('[data-option-filter-status]');
      this.container.hidden = !hasVisibleGroups;
    }

    restoreOriginalGrid() {
      if (this.gridItemsContainer) {
        this.gridItemsContainer.innerHTML = this.originalGridHTML;
      }

      if (this.paginationWrapper) {
        this.paginationWrapper.hidden = false;
      }
    }

    renderFilteredGrid(records) {
      if (this.gridItemsContainer) {
        this.gridItemsContainer.innerHTML = records.map((record) => record.html).join('');
      }

      if (this.paginationWrapper) {
        this.paginationWrapper.hidden = true;
      }
    }

    updateProductCount(visibleCount, hasActiveFilters) {
      if (!hasActiveFilters) {
        if (this.productCount) {
          this.productCount.innerHTML = this.originalCountHTML;
        }

        if (this.productCountDesktop) {
          this.productCountDesktop.innerHTML = this.originalCountDesktopHTML;
        }

        return;
      }

      const text = getProductCountLabel(visibleCount);

      if (this.productCount) {
        this.productCount.textContent = text;
      }

      if (this.productCountDesktop) {
        this.productCountDesktop.textContent = text;
      }
    }

    applyFilters() {
      const hasActiveFilters = FILTER_CONFIGS.some(({ key }) => this.state[key].size > 0);
      const matchingRecords = this.records.filter((record) => this.cardMatchesState(record, this.state));
      const visibleCount = matchingRecords.length;

      if (hasActiveFilters) {
        this.renderFilteredGrid(matchingRecords);
        this.gridShowsOriginal = false;
      } else if (!this.gridShowsOriginal) {
        this.restoreOriginalGrid();
        this.gridShowsOriginal = true;
      }

      if (this.clearButton) {
        this.clearButton.hidden = !hasActiveFilters;
      }

      this.updateProductCount(visibleCount, hasActiveFilters);

      if (this.statusElement) {
        if (this.isLoadingCollection) {
          this.statusElement.textContent = 'Loading full collection...';
          this.statusElement.hidden = false;
        } else if (hasActiveFilters && visibleCount === 0) {
          this.statusElement.textContent = `No matching products in this ${this.hasCollectionData ? 'collection' : 'page'}`;
          this.statusElement.hidden = false;
        } else if (hasActiveFilters && this.hasCollectionData) {
          this.statusElement.textContent = `Showing ${visibleCount} matching ${visibleCount === 1 ? 'product' : 'products'} in this collection`;
          this.statusElement.hidden = false;
        } else {
          this.statusElement.textContent = '';
          this.statusElement.hidden = true;
        }
      }
    }

    syncHiddenInputs() {
      FILTER_CONFIGS.forEach(({ key, param }) => {
        const value = Array.from(this.state[key]).join(',');

        document.querySelectorAll(`input[name="${param}"]`).forEach((input) => {
          input.value = value;
        });
      });
    }

    updateLinkedUrls() {
      const selectors = [
        '#main-collection-filters a[href]',
        '#main-search-filters a[href]',
        '#ProductGridContainer .pagination a[href]',
      ];

      document.querySelectorAll(selectors.join(',')).forEach((anchor) => {
        const href = anchor.getAttribute('href');

        if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

        const url = new URL(href, window.location.origin);

        FILTER_CONFIGS.forEach(({ key, param }) => {
          const value = Array.from(this.state[key]).join(',');

          if (value) {
            url.searchParams.set(param, value);
          } else {
            url.searchParams.delete(param);
          }
        });

        anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      });
    }

    updateUrl() {
      const params = new URLSearchParams(window.location.search);

      FILTER_CONFIGS.forEach(({ key, param }) => {
        const value = Array.from(this.state[key]).join(',');

        if (value) {
          params.set(param, value);
        } else {
          params.delete(param);
        }
      });

      const searchParams = params.toString();
      const nextUrl = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
      window.history.replaceState({ searchParams }, '', nextUrl);
    }

    rerender() {
      this.render();
      this.applyFilters();
      this.syncHiddenInputs();
      this.updateLinkedUrls();
      this.updateUrl();
    }

    handleClick(event) {
      const clearButton = event.target.closest('[data-option-filter-clear]');
      if (clearButton) {
        FILTER_CONFIGS.forEach(({ key }) => {
          this.state[key].clear();
        });

        this.rerender();
        return;
      }

      const clearGroupButton = event.target.closest('[data-option-filter-clear-group]');
      if (clearGroupButton) {
        const filterKey = clearGroupButton.dataset.optionFilterClearGroup;

        if (this.state[filterKey]) {
          this.state[filterKey].clear();
          this.rerender();
        }

        return;
      }

      const valueButton = event.target.closest('[data-option-filter-value]');
      if (!valueButton) return;

      const filterKey = valueButton.dataset.filterKey;
      const filterValue = valueButton.dataset.optionFilterValue;
      const selectedValues = this.state[filterKey];

      if (!selectedValues) return;

      if (selectedValues.has(filterValue)) {
        selectedValues.delete(filterValue);
      } else {
        selectedValues.add(filterValue);
      }

      this.rerender();
    }

    handleChange(event) {
      const valueInput = event.target.closest('[data-option-filter-value]');
      if (!valueInput) return;

      const filterKey = valueInput.dataset.filterKey;
      const filterValue = valueInput.dataset.optionFilterValue;
      const selectedValues = this.state[filterKey];

      if (!selectedValues) return;

      if (valueInput.checked) {
        selectedValues.add(filterValue);
      } else {
        selectedValues.delete(filterValue);
      }

      this.rerender();
    }

    handleSearchInput(event) {
      const searchInput = event.target.closest('[data-option-filter-search]');
      if (!searchInput) return;

      const query = searchInput.value.trim().toLowerCase();
      const groupElement = searchInput.closest('.collection-option-filter');
      if (!groupElement) return;

      const optionItems = groupElement.querySelectorAll('.facets__item');

      optionItems.forEach((li) => {
        const textLabel = li.querySelector('.facet-checkbox__text-label')?.textContent?.trim().toLowerCase() || '';
        if (!query || textLabel.includes(query)) {
          li.style.display = '';
        } else {
          li.style.display = 'none';
        }
      });
    }
  }

  function initializeCollectionOptionFilters() {
    document.querySelectorAll('[data-collection-option-filters]').forEach((container) => {
      if (container._collectionOptionFilters) {
        container._collectionOptionFilters.destroy();
      }

      container._collectionOptionFilters = new CollectionOptionFilters(container);
    });
  }

  function observeCollectionGridUpdates() {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer || gridContainer._collectionOptionObserver) return;

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(initializeCollectionOptionFilters);
    });

    observer.observe(gridContainer, { childList: true });
    gridContainer._collectionOptionObserver = observer;
  }

  document.addEventListener('DOMContentLoaded', () => {
    initializeCollectionOptionFilters();
    observeCollectionGridUpdates();
  });

  document.addEventListener('shopify:section:load', () => {
    initializeCollectionOptionFilters();
    observeCollectionGridUpdates();
  });

  window.addEventListener('popstate', () => {
    window.requestAnimationFrame(initializeCollectionOptionFilters);
  });
})();
